import { cn } from '@up2stay/up2promo-ui';
import {
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { useIsMobile } from '@/core/hooks/use-mobile';
import { useDictionary } from '@/core/providers/dictionary-provider';

/**
 * Detect a "real desktop" input environment (mouse / fine pointer). The
 * minimap is mouse-only — touch users on phone or tablet scroll the board
 * directly with their finger.
 */
function useHasFinePointer(): boolean {
  const [hasFinePointer, setHasFinePointer] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia('(hover: hover) and (pointer: fine)');
    const update = () => setHasFinePointer(mql.matches);
    update();
    mql.addEventListener('change', update);
    return () => mql.removeEventListener('change', update);
  }, []);

  return hasFinePointer;
}

interface KanbanMinimapColumn {
  id: string;
  /** Status color used as accent for this column inside the minimap. */
  color: string;
  /** Card count used to render mini-card blocks inside the column. */
  cardCount: number;
}

interface KanbanMinimapProps {
  /** Ref to the horizontally scrollable board container. */
  scrollContainerRef: RefObject<HTMLDivElement | null>;
  /** Ordered list of columns (left → right) shown in the board. */
  columns: ReadonlyArray<KanbanMinimapColumn>;
  className?: string;
}

interface ScrollMetrics {
  scrollLeft: number;
  scrollWidth: number;
  clientWidth: number;
}

const MINIMAP_WIDTH = 240;
/** Cap mini-card stack height per column so very tall columns stay readable. */
const MAX_MINI_CARDS = 5;

/**
 * Bottom-right minimap that mirrors the kanban board's horizontal extent and
 * provides a draggable viewport indicator. Only visible on desktop and only
 * when the board actually overflows its container.
 */
export function KanbanMinimap({
  scrollContainerRef,
  columns,
  className,
}: KanbanMinimapProps) {
  const dict = useDictionary();
  const isMobile = useIsMobile();
  const hasFinePointer = useHasFinePointer();
  const [metrics, setMetrics] = useState<ScrollMetrics>({
    scrollLeft: 0,
    scrollWidth: 0,
    clientWidth: 0,
  });

  const minimapRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);
  const dragStartRef = useRef({ pointerX: 0, scrollLeft: 0 });

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    let frame: number | null = null;
    const update = () => {
      if (frame != null) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        setMetrics({
          scrollLeft: container.scrollLeft,
          scrollWidth: container.scrollWidth,
          clientWidth: container.clientWidth,
        });
      });
    };

    update();
    container.addEventListener('scroll', update, { passive: true });

    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(container);
    // Observe direct children too: their width changes (e.g. when cards are
    // added/removed) affect scrollWidth without firing scroll events.
    Array.from(container.children).forEach((child) => {
      resizeObserver.observe(child);
    });

    return () => {
      if (frame != null) cancelAnimationFrame(frame);
      container.removeEventListener('scroll', update);
      resizeObserver.disconnect();
    };
  }, [scrollContainerRef]);

  /** Pixels of board per pixel of minimap (0 when not yet measured). */
  const boardPerMinimapPx =
    metrics.scrollWidth > 0 ? metrics.scrollWidth / MINIMAP_WIDTH : 0;
  const viewportWidth =
    boardPerMinimapPx > 0 ? metrics.clientWidth / boardPerMinimapPx : 0;
  const viewportLeft =
    boardPerMinimapPx > 0 ? metrics.scrollLeft / boardPerMinimapPx : 0;

  const handleIndicatorPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const container = scrollContainerRef.current;
      if (!container) return;
      e.preventDefault();
      e.stopPropagation();
      isDraggingRef.current = true;
      dragStartRef.current = {
        pointerX: e.clientX,
        scrollLeft: container.scrollLeft,
      };
      e.currentTarget.setPointerCapture(e.pointerId);
    },
    [scrollContainerRef],
  );

  const handleIndicatorPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!isDraggingRef.current) return;
      const container = scrollContainerRef.current;
      const minimap = minimapRef.current;
      if (!container || !minimap) return;
      // Compute ratio from live DOM measurements to avoid stale-state issues
      // during fast drags (state updates lag behind pointer events).
      const currentRatio = container.scrollWidth / minimap.clientWidth;
      if (!Number.isFinite(currentRatio) || currentRatio <= 0) return;
      const dx = e.clientX - dragStartRef.current.pointerX;
      const maxScroll = container.scrollWidth - container.clientWidth;
      const target = dragStartRef.current.scrollLeft + dx * currentRatio;
      container.scrollLeft = Math.max(0, Math.min(maxScroll, target));
    },
    [scrollContainerRef],
  );

  const handleIndicatorPointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!isDraggingRef.current) return;
      isDraggingRef.current = false;
      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId);
      }
    },
    [],
  );

  /** Click-on-track: jump-scroll so the click lands in the centre of the viewport indicator. */
  const handleTrackClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const container = scrollContainerRef.current;
      const minimap = minimapRef.current;
      if (!container || !minimap) return;
      const rect = minimap.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const ratio = container.scrollWidth / minimap.clientWidth;
      if (!Number.isFinite(ratio) || ratio <= 0) return;
      const targetCentre = clickX * ratio;
      const maxScroll = container.scrollWidth - container.clientWidth;
      const target = Math.max(
        0,
        Math.min(maxScroll, targetCentre - container.clientWidth / 2),
      );
      container.scrollTo({ left: target, behavior: 'smooth' });
    },
    [scrollContainerRef],
  );

  const hasOverflow = metrics.scrollWidth > metrics.clientWidth + 1;
  // Hide on touch devices (phones, tablets, Capacitor native) — they scroll
  // with their finger. Only show when the board actually overflows.
  if (isMobile || !hasFinePointer || !hasOverflow) return null;

  const ariaLabel = dict.kanban?.a11y?.minimap?.label ?? 'Kanban board minimap';
  const ariaViewportLabel =
    dict.kanban?.a11y?.minimap?.viewport ??
    'Drag to scroll the kanban board horizontally';

  return (
    <div
      className={cn(
        'pointer-events-auto fixed bottom-6 right-6 z-40',
        'rounded-lg border bg-card/95 p-2 shadow-lg backdrop-blur',
        'supports-[backdrop-filter]:bg-card/80',
        className,
      )}
      role="region"
      aria-label={ariaLabel}
    >
      <div
        ref={minimapRef}
        className="relative cursor-pointer select-none"
        style={{ width: MINIMAP_WIDTH }}
        onClick={handleTrackClick}
      >
        {/* Mini board: equal-width column blocks with status accent + mini cards */}
        <div className="flex h-14 gap-[2px] overflow-hidden rounded-sm bg-muted/30 p-1">
          {columns.map((column) => {
            const visibleCards = Math.min(column.cardCount, MAX_MINI_CARDS);
            return (
              <div
                key={column.id}
                className="flex flex-1 flex-col gap-[2px] rounded-sm bg-background/40 p-[3px]"
              >
                <div
                  className="h-[3px] shrink-0 rounded-full"
                  style={{ backgroundColor: column.color }}
                  aria-hidden
                />
                {Array.from({ length: visibleCards }).map((_, i) => (
                  <div
                    key={i}
                    className="h-[3px] rounded-[1px] bg-muted-foreground/40"
                    aria-hidden
                  />
                ))}
              </div>
            );
          })}
        </div>

        {/* Viewport indicator — overlays the mini board */}
        <div
          role="slider"
          tabIndex={0}
          aria-label={ariaViewportLabel}
          aria-valuemin={0}
          aria-valuemax={Math.max(0, metrics.scrollWidth - metrics.clientWidth)}
          aria-valuenow={metrics.scrollLeft}
          className={cn(
            'absolute inset-y-0 cursor-grab rounded-sm border-2 border-primary',
            'bg-primary/10 transition-colors hover:bg-primary/15',
            'active:cursor-grabbing active:bg-primary/20',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
          )}
          style={{
            left: Math.max(0, viewportLeft),
            width: Math.max(8, viewportWidth),
          }}
          onClick={(e) => e.stopPropagation()}
          onPointerDown={handleIndicatorPointerDown}
          onPointerMove={handleIndicatorPointerMove}
          onPointerUp={handleIndicatorPointerUp}
          onPointerCancel={handleIndicatorPointerUp}
          onKeyDown={(e) => {
            const container = scrollContainerRef.current;
            if (!container) return;
            const step = Math.max(48, Math.round(container.clientWidth * 0.15));
            if (e.key === 'ArrowLeft') {
              e.preventDefault();
              container.scrollBy({ left: -step, behavior: 'smooth' });
            } else if (e.key === 'ArrowRight') {
              e.preventDefault();
              container.scrollBy({ left: step, behavior: 'smooth' });
            } else if (e.key === 'Home') {
              e.preventDefault();
              container.scrollTo({ left: 0, behavior: 'smooth' });
            } else if (e.key === 'End') {
              e.preventDefault();
              container.scrollTo({
                left: container.scrollWidth,
                behavior: 'smooth',
              });
            }
          }}
        />
      </div>
    </div>
  );
}
